import React, { useEffect, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  CssBaseline,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Collapse,
  Typography,
  Divider,
  Tooltip,
  Button,
  Drawer as MuiDrawer,
  AppBar as MuiAppBar,
  useMediaQuery,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { Link, useLocation } from "react-router-dom";
import { navConfig } from "./Menuitem";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTopProducts,
  fetchChildItems,
  toggleOpen,
  expandPathByUIDs,
} from "../../store/reducers/productSlice";
import ProfileMenu from "./ProfileMenu";
import Cookies from "js-cookie";
import { sidebarIcons } from "./icons";
import { useLogger } from "../../hooks/useLogger";
import Support from "./Support";

const drawerWidth = 280;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: open ? drawerWidth : 100,
  width: "100%",
  height: "80px",
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: open ? drawerWidth : 100,
  flexShrink: 0,
  whiteSpace: "nowrap",
  "& .MuiDrawer-paper": {
    width: open ? drawerWidth : 100,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    border: "none",
    overflowX: "hidden",
  },
}));

const ToggleButton = styled(IconButton)(({ theme, open }) => ({
  transition: theme.transitions.create(["left"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  position: "fixed",
  left: open ? "8.6em" : "3.2em",
  top: "4em",
  background: "#fff",
  border: "5px solid #f9f9f9",
  width: "55px",
  height: "55px",
  zIndex: 2,
  "&:hover": {
    background: "#fff",
  },
}));

export default function MiniDrawer({
  open,
  setOpen,
  openSideBar,
  setOpenSideBar,
}) {
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const token = Cookies.get("_d_token");
  const comToken = Cookies.get("c_d_token");
  const companyDetails = useSelector(
    (state) => state.companydetail.companyDetails
  );
  const products = useSelector((state) => state.products.items);
  const childrenMap = useSelector((state) => state.products.childrenMap);
  const openMap = useSelector((state) => state.products.openMap);
  const logEvent = useLogger();
  const [premiumPopupOpen, setPremiumPopupOpen] = useState(false);

  const homeItem = {
    id: 0,
    type: "static",
    path: "",
    title: "Home",
    iconKey: "home",
  };

  const filteredNavConfig = navConfig.filter((item) => {
    if (companyDetails?.data?.role === "OWNER") return true;
    if (companyDetails?.data?.role === "ADMIN") return item.title !== "Users";
    if (companyDetails?.data?.role === "USER") return false;
    return false;
  });

  const [openSubmenu, setOpenSubmenu] = useState({});

  const handleToggleSubmenu = (id) => {
    if (open) {
      setOpenSubmenu((prev) => ({
        ...prev,
        [id]: !prev[id],
      }));
    }
  };

  const handleDynamicToggle = (item) => {
    const uid = `${item.type}-${item.id}`;
    dispatch(toggleOpen(uid));
    if (!openMap[uid]) return dispatch(fetchChildItems(item));
    return Promise.resolve();
  };

  const getIconColor = (item) => {
    const currentPath = decodeURIComponent(location.pathname);
    if (`/${item.path}` === currentPath) return "#FFFFFF";
    if (currentPath.includes(`/${item.path}`)) return "#185CB7";
    return "#1D1D1D";
  };

  const isPathInTree = (
    item,
    childrenMap,
    currentPath,
    visited = new Set()
  ) => {
    const uid = `${item.type}-${item.id}`;
    if (visited.has(uid)) return false;
    visited.add(uid);
    if (`/${item.path}` === currentPath) return true;
    const children = childrenMap[uid] || [];
    return children.some((child) =>
      isPathInTree(child, childrenMap, currentPath, visited)
    );
  };

  const renderDynamicItems = (items, level = 0) =>
    items.map((item, index) => {
      const uid = `${item.type}-${item.id}`;
      const currentPath = decodeURIComponent(location.pathname);
      const isSelfActive = currentPath === `/${item.path}`;
      const isActive = isPathInTree(item, childrenMap, currentPath);
      const isHome = item.type === "static" && item.path === "";

      const getItemStyles = () => {
        if (isSelfActive) {
          return {
            color: "#FFFFFF",
            background: "linear-gradient(-90deg, #2257AD 0%, #B01121 100%)",
          };
        }
        if (isActive) {
          return {
            color: "#185CB7",
            background:
              "linear-gradient(90deg,rgba(34, 87, 173, 0.06) 0%, rgba(176, 17, 33, 0.06) 100%)",
          };
        }
        return {
          color: "#1D1D1D",
          background: "transparent",
        };
      };

      return (
        <React.Fragment key={uid}>
          <Box
            sx={{
              position: "relative",
              mt: level === 1 ? "12px" : "0px",
              ml: level > 1 ? `${level * 16}px` : 0,
              // vertical connector |
              "&::before":
                level > 1
                  ? {
                      content: '""',
                      position: "absolute",
                      top: "-12px",
                      left: "-16px",
                      // 👇 stops the line if it's the last child
                      bottom: index === items.length - 1 ? "50%" : 0,
                      width: "1.5px",
                      backgroundColor: "#E0E0E0",
                    }
                  : {},
            }}
          >
            {/* Horizontal connector _ */}
            {level > 1 && (
              <Box
                sx={{
                  position: "absolute",
                  top: "24px",
                  left: "-16px",
                  width: "24px",
                  height: "1.5px",
                  backgroundColor: "#E0E0E0",
                }}
              />
            )}

            <Link
              to={`/${item.path}`}
              onClick={() =>
                isMobile &&
                item.type == "endpoint" &&
                setOpenSideBar(!openSideBar)
              }
              style={{ textDecoration: "none", color: "inherit" }}
            >
              {level <= 0 && (
                <Box
                  sx={{
                    height: "48px",
                    width: "27px",
                    ...getItemStyles(),
                    position: "absolute",
                    left: "-15px",
                    borderRadius: "0 320px 320px 0",
                  }}
                />
              )}
              <ListItemButton
                disableRipple
                sx={{
                  pl: 1 + level * 0.1,
                  ml:
                    open && level <= 0 ? "10px" : open && level > 0 ? "0" : "",
                  justifyContent: open ? "initial" : "center",
                  height: "48px",
                  mt:
                    level < 1 ||
                    (index === 0 && level <= 1) ||
                    (index === 0 && level >= 2)
                      ? "12px"
                      : 0,
                  pr: 0,
                }}
                onClick={() => handleDynamicToggle(item)}
              >
                <Box
                  sx={{
                    ...getItemStyles(),
                    borderRadius: "320px 0 0 320px",
                    display: "flex",
                    alignItems: "center",
                    height: 48,
                    width: "100%",
                    p: "0 16px",
                    pl:
                      level > 0 && level < 2
                        ? `${level * 33}px`
                        : level > 1
                        ? `${level * 5}px`
                        : "16px",
                    ml: level > 0 ? "8px" : "",
                    maxWidth: isMobile ? "auto" : "100%",
                  }}
                >
                  {item.iconKey && (
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        justifyContent: "center",
                        mr: open ? "10px" : "",
                        background: !open
                          ? "linear-gradient(to right, #2257AD, #B01121)"
                          : "transparent",
                        width: !open ? "50px" : "auto",
                        height: !open ? "50px" : "auto",
                        borderRadius: !open ? "320px" : "",
                      }}
                    >
                      {sidebarIcons[item.iconKey]?.(
                        isHome
                          ? isSelfActive
                            ? "#FFFFFF"
                            : "#000000"
                          : getIconColor(item)
                      )}
                    </ListItemIcon>
                  )}
                  <Tooltip title={item.title} arrow placement="right">
                    <ListItemText
                      primary={item.title}
                      sx={{
                        opacity: open ? 1 : 0,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "100%",
                        transition: "max-width 0.3s ease",
                        "& .MuiTypography-root": {
                          fontSize: "12px",
                          fontWeight: "500",
                          whiteSpace: level > 1 ? "" : "nowrap",
                          overflow: level > 1 ? "" : "hidden",
                          textOverflow: level > 1 ? "" : "ellipsis",
                          textWrap: level > 1 ? "auto" : "",
                        },
                      }}
                    />
                  </Tooltip>
                  {item.type !== "endpoint" &&
                    !isHome &&
                    (level === 0 ? item.has_child_items : true) &&
                    (openMap[uid] ? (
                      <Icon icon="mdi:chevron-up" />
                    ) : (
                      <Icon icon="mdi:chevron-down" />
                    ))}
                </Box>
              </ListItemButton>
            </Link>
            {item.type !== "endpoint" &&
              !isHome &&
              (level === 0 ? item.has_child_items : true) && (
                <Collapse in={openMap[uid]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 2 }}>
                    {childrenMap[uid] ? (
                      renderDynamicItems(childrenMap[uid], level + 1)
                    ) : (
                      <Typography sx={{ pl: 4 + level * 2, color: "#999" }}>
                        Loading...
                      </Typography>
                    )}
                  </List>
                </Collapse>
              )}
          </Box>
        </React.Fragment>
      );
    });

  useEffect(() => {
    dispatch(fetchTopProducts());
  }, [dispatch]);

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          open={open}
          sx={{
            background: "#fff",
            color: "#000",
            boxShadow:
              "rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgb(209, 213, 219) 0px 0px 0px 0px inset",
          }}
        >
          <Toolbar
            sx={{
              height: "80px",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/meon-tech.png`}
                alt="Logo"
                style={{ height: 35, cursor: "pointer" }}
                onClick={() => {
                  window.location.replace(
                    `${process.env.PUBLIC_URL}/new/login`
                  );
                }}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: "20px" }}>
                {(!isMobile || (isMobile && !token && !comToken)) && (
                  <ProfileMenu setPremiumPopupOpen={setPremiumPopupOpen} />
                )}
                {isMobile && (
                  <>
                    {(token || comToken) && (
                      <Button
                        variant="contained"
                        sx={{
                          height: 42,
                          color: "#000000",
                          background: "none",
                          textTransform: "none",
                          fontSize: "16px",
                          borderRadius: "10px",
                          border: "none",
                          boxShadow: "none",
                          fontWeight: "500",
                          gap: "4px",
                        }}
                        onClick={() => setPremiumPopupOpen(true)}
                      >
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 22 22"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4.98851 15.2948L4.63119 16.139H4.63119L4.98851 15.2948ZM4.98851 8.53853L5.34584 9.38269L4.98851 8.53853ZM5.67397 8.41743L5.5 9.31743L5.67397 8.41743ZM6.261 8.8064L7.022 8.29536L6.261 8.8064ZM5.67397 15.4159L5.5 14.5159H5.5L5.67397 15.4159ZM6.261 15.0269L5.5 14.5159L5.5 14.5159L6.261 15.0269ZM17.0115 15.2948L17.3688 16.139L17.0115 15.2948ZM17.0115 8.53853L16.6542 9.38269L17.0115 8.53853ZM16.326 8.41743L16.5 9.31743L16.326 8.41743ZM15.739 8.8064L14.978 8.29536H14.978L15.739 8.8064ZM16.326 15.4159L16.5 14.5159H16.5L16.326 15.4159ZM15.739 15.0269L16.5 14.5159V14.5159L15.739 15.0269ZM18.3333 15.5833C18.3333 15.0771 17.9229 14.6667 17.4167 14.6667C16.9104 14.6667 16.5 15.0771 16.5 15.5833H17.4167H18.3333ZM11 17.4167C10.4937 17.4167 10.0833 17.8271 10.0833 18.3333C10.0833 18.8396 10.4937 19.25 11 19.25V18.3333V17.4167ZM4.98851 15.2948L5.34583 14.4506C4.35736 14.0322 3.66667 13.0541 3.66667 11.9167H2.75H1.83333C1.83333 13.8161 2.9886 15.4437 4.63119 16.139L4.98851 15.2948ZM2.75 11.9167H3.66667C3.66667 10.7793 4.35736 9.8011 5.34584 9.38269L4.98851 8.53853L4.63119 7.69438C2.9886 8.38967 1.83333 10.0172 1.83333 11.9167H2.75ZM6.41667 14.1167H7.33333V9.71667H6.41667H5.5V14.1167H6.41667ZM4.98851 8.53853L5.34584 9.38269C5.41901 9.35172 5.46058 9.33428 5.49526 9.32125C5.52659 9.30947 5.53308 9.30878 5.52632 9.31026C5.51607 9.31249 5.50097 9.31438 5.48651 9.31433C5.47428 9.31429 5.47623 9.31284 5.5 9.31743L5.67397 8.41743L5.84794 7.51742C5.60828 7.47109 5.37843 7.46616 5.1362 7.51891C4.9328 7.56321 4.74535 7.64606 4.63119 7.69438L4.98851 8.53853ZM6.41667 9.71667H7.33333C7.33333 9.3386 7.36261 8.80257 7.022 8.29536L6.261 8.8064L5.5 9.31743C5.50007 9.31754 5.49822 9.31477 5.49555 9.30952C5.49283 9.30418 5.49036 9.29845 5.48842 9.29304C5.4843 9.28159 5.48603 9.28138 5.48902 9.30325C5.49241 9.32797 5.49593 9.36913 5.49791 9.44142C5.49993 9.5147 5.5 9.60002 5.5 9.71667H6.41667ZM5.67397 8.41743L5.5 9.31743C5.48628 9.31478 5.47543 9.31208 5.46767 9.30995C5.45985 9.3078 5.45413 9.30594 5.45061 9.30472C5.44394 9.30243 5.44258 9.30156 5.44642 9.30333C5.45002 9.30499 5.45631 9.30811 5.46479 9.31289C5.47319 9.31762 5.48243 9.32325 5.49196 9.32957C5.50149 9.33588 5.51028 9.3422 5.51791 9.34809C5.52562 9.35404 5.53094 9.35861 5.53387 9.36128C5.537 9.36413 5.53567 9.3632 5.53096 9.35796C5.52847 9.35519 5.52453 9.35065 5.5195 9.34429C5.51451 9.33798 5.50779 9.32903 5.5 9.31743L6.261 8.8064L7.022 8.29536C6.87319 8.07376 6.66348 7.90655 6.50462 7.80128C6.34575 7.69602 6.11002 7.56808 5.84794 7.51742L5.67397 8.41743ZM4.98851 15.2948L4.63119 16.139C4.74535 16.1873 4.9328 16.2701 5.1362 16.3144C5.37843 16.3672 5.60828 16.3622 5.84794 16.3159L5.67397 15.4159L5.5 14.5159C5.47623 14.5205 5.47428 14.519 5.48651 14.519C5.50097 14.5189 5.51607 14.5208 5.52632 14.5231C5.53308 14.5245 5.52659 14.5239 5.49526 14.5121C5.46058 14.499 5.41901 14.4816 5.34583 14.4506L4.98851 15.2948ZM6.41667 14.1167H5.5C5.5 14.2333 5.49993 14.3186 5.49791 14.3919C5.49593 14.4642 5.49241 14.5054 5.48902 14.5301C5.48603 14.552 5.4843 14.5517 5.48842 14.5403C5.49036 14.5349 5.49283 14.5291 5.49555 14.5238C5.49822 14.5186 5.50007 14.5158 5.5 14.5159L6.261 15.0269L7.022 15.538C7.36261 15.0308 7.33333 14.4947 7.33333 14.1167H6.41667ZM5.67397 15.4159L5.84794 16.3159C6.11002 16.2653 6.34575 16.1373 6.50462 16.032C6.66348 15.9268 6.87319 15.7596 7.022 15.538L6.261 15.0269L5.5 14.5159C5.50779 14.5043 5.51451 14.4954 5.5195 14.489C5.52453 14.4827 5.52847 14.4781 5.53096 14.4754C5.53567 14.4701 5.537 14.4692 5.53387 14.472C5.53094 14.4747 5.52562 14.4793 5.51791 14.4852C5.51028 14.4911 5.50149 14.4975 5.49196 14.5038C5.48243 14.5101 5.47319 14.5157 5.46479 14.5204C5.45631 14.5252 5.45002 14.5283 5.44642 14.53C5.44258 14.5318 5.44394 14.5309 5.45061 14.5286C5.45413 14.5274 5.45985 14.5255 5.46767 14.5234C5.47543 14.5213 5.48628 14.5185 5.5 14.5159L5.67397 15.4159ZM17.0115 15.2948L17.3688 16.139C19.0114 15.4437 20.1667 13.8161 20.1667 11.9167H19.25H18.3333C18.3333 13.0541 17.6426 14.0322 16.6542 14.4506L17.0115 15.2948ZM19.25 11.9167H20.1667C20.1667 10.0172 19.0114 8.38967 17.3688 7.69438L17.0115 8.53853L16.6542 9.38269C17.6426 9.8011 18.3333 10.7793 18.3333 11.9167H19.25ZM15.5833 14.1167H16.5V9.71667H15.5833H14.6667V14.1167H15.5833ZM17.0115 8.53853L17.3688 7.69438C17.2547 7.64606 17.0672 7.56321 16.8638 7.51891C16.6216 7.46616 16.3917 7.47109 16.1521 7.51742L16.326 8.41743L16.5 9.31743C16.5238 9.31284 16.5257 9.31429 16.5135 9.31433C16.499 9.31438 16.4839 9.31249 16.4737 9.31026C16.4669 9.30878 16.4734 9.30947 16.5047 9.32125C16.5394 9.33428 16.581 9.35172 16.6542 9.38269L17.0115 8.53853ZM15.5833 9.71667H16.5C16.5 9.60002 16.5001 9.5147 16.5021 9.44142C16.5041 9.36913 16.5076 9.32797 16.511 9.30325C16.514 9.28138 16.5157 9.28159 16.5116 9.29304C16.5096 9.29845 16.5072 9.30418 16.5045 9.30952C16.5018 9.31477 16.4999 9.31754 16.5 9.31743L15.739 8.8064L14.978 8.29536C14.6374 8.80257 14.6667 9.3386 14.6667 9.71667H15.5833ZM16.326 8.41743L16.1521 7.51742C15.89 7.56808 15.6542 7.69602 15.4954 7.80128C15.3365 7.90655 15.1268 8.07376 14.978 8.29536L15.739 8.8064L16.5 9.31743C16.4922 9.32903 16.4855 9.33798 16.4805 9.34429C16.4755 9.35065 16.4715 9.35519 16.469 9.35796C16.4643 9.36321 16.463 9.36413 16.4661 9.36128C16.4691 9.35861 16.4744 9.35404 16.4821 9.34809C16.4897 9.3422 16.4985 9.33588 16.508 9.32957C16.5176 9.32325 16.5268 9.31762 16.5352 9.31289C16.5437 9.30811 16.55 9.30499 16.5536 9.30333C16.5574 9.30156 16.5561 9.30243 16.5494 9.30472C16.5459 9.30594 16.5402 9.3078 16.5323 9.30995C16.5246 9.31208 16.5137 9.31478 16.5 9.31743L16.326 8.41743ZM17.0115 15.2948L16.6542 14.4506C16.581 14.4816 16.5394 14.499 16.5047 14.5121C16.4734 14.5239 16.4669 14.5245 16.4737 14.5231C16.4839 14.5208 16.499 14.5189 16.5135 14.519C16.5257 14.519 16.5238 14.5205 16.5 14.5159L16.326 15.4159L16.1521 16.3159C16.3917 16.3622 16.6216 16.3672 16.8638 16.3144C17.0672 16.2701 17.2547 16.1873 17.3688 16.139L17.0115 15.2948ZM15.5833 14.1167H14.6667C14.6667 14.4947 14.6374 15.0308 14.978 15.538L15.739 15.0269L16.5 14.5159C16.4999 14.5158 16.5018 14.5186 16.5045 14.5238C16.5072 14.5291 16.5096 14.5349 16.5116 14.5403C16.5157 14.5517 16.514 14.552 16.511 14.5301C16.5076 14.5054 16.5041 14.4642 16.5021 14.3919C16.5001 14.3186 16.5 14.2333 16.5 14.1167H15.5833ZM16.326 15.4159L16.5 14.5159C16.5137 14.5185 16.5246 14.5213 16.5323 14.5234C16.5402 14.5255 16.5459 14.5274 16.5494 14.5286C16.5561 14.5309 16.5574 14.5318 16.5536 14.53C16.55 14.5283 16.5437 14.5252 16.5352 14.5204C16.5268 14.5157 16.5176 14.5101 16.508 14.5038C16.4985 14.4975 16.4897 14.4911 16.4821 14.4852C16.4744 14.4793 16.4691 14.4747 16.4661 14.472C16.463 14.4692 16.4643 14.4701 16.469 14.4754C16.4715 14.4781 16.4755 14.4827 16.4805 14.489C16.4855 14.4954 16.4922 14.5043 16.5 14.5159L15.739 15.0269L14.978 15.538C15.1268 15.7596 15.3365 15.9268 15.4954 16.032C15.6542 16.1373 15.89 16.2653 16.1521 16.3159L16.326 15.4159ZM4.58333 9.16667H5.5C5.5 6.1291 7.96243 3.66667 11 3.66667V2.75V1.83333C6.94991 1.83333 3.66667 5.11658 3.66667 9.16667H4.58333ZM11 2.75V3.66667C14.0376 3.66667 16.5 6.1291 16.5 9.16667H17.4167H18.3333C18.3333 5.11658 15.0501 1.83333 11 1.83333V2.75ZM17.4167 15.5833H16.5V16.5H17.4167H18.3333V15.5833H17.4167ZM15.5833 18.3333V17.4167H11V18.3333V19.25H15.5833V18.3333ZM17.4167 16.5H16.5C16.5 17.0063 16.0896 17.4167 15.5833 17.4167V18.3333V19.25C17.1021 19.25 18.3333 18.0188 18.3333 16.5H17.4167Z"
                            fill="#D50B34"
                          />
                        </svg>
                      </Button>
                    )}
                    <IconButton
                      edge="start"
                      onClick={() => setOpenSideBar(!openSideBar)}
                    >
                      {openSideBar ? (
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M20 12L10 12"
                            stroke="#1D1D1D"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M20 5L4 5"
                            stroke="#1D1D1D"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M20 19L4 19"
                            stroke="#1D1D1D"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          width="24"
                          height="24"
                          color="#000000"
                          fill="none"
                        >
                          <path
                            d="M18 6L6.00081 17.9992M17.9992 18L6 6.00085"
                            stroke="#141B34"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      )}
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Desktop Sidebar */}
        <Drawer
          variant="permanent"
          open={open}
          sx={{ display: { xs: "none", sm: "block" } }}
        >
          <List sx={{ marginTop: "80px" }}>
            {/* <ToggleButton open={open} onClick={() => setOpen(!open)}>
              {open ? <svg width="10" height="17" viewBox="0 0 10 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.55084 14.983L2.06779 8.49998L8.55084 2.01693" stroke="#185CB7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg> : <svg width="10" height="17" viewBox="0 0 10 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.06779 14.983L8.55085 8.5L2.06779 2.01695" stroke="#185CB7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              }
          </ToggleButton> */}
            {token &&
              filteredNavConfig.map((item) => {
                const isActive =
                  location.pathname === `/${item.path}` ||
                  (item.children &&
                    item.children.some(
                      (sub) => `/${sub.path}` === location.pathname
                    ));
                return (
                  <React.Fragment key={item.id}>
                    <ListItem
                      disablePadding
                      sx={{ display: "block", mt: "12px" }}
                    >
                      {item.children ? (
                        <>
                          <ListItemButton
                            sx={{
                              ml: open ? "15px" : "",
                              justifyContent: open ? "initial" : "center",
                              height: "48px",
                            }}
                            onClick={() => {
                              logEvent(
                                "Click",
                                `open the dropdown of ${item.title} menu`,
                                "header"
                              );
                              handleToggleSubmenu(item.id);
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                justifyContent: "center",
                                mr: open ? "15px" : "",
                                background:
                                  isActive && !open
                                    ? "linear-gradient(to right, #2257AD, #B01121)"
                                    : open
                                    ? "transparent"
                                    : "#F4F4F4",
                                width: !open ? "50px" : "Auto",
                                height: !open ? "50px" : "Auto",
                                borderRadius: !open ? "320px" : "",
                                alignItems: !open ? "center" : "",
                              }}
                            >
                              {item.icon(
                                isActive && !open ? "#fff" : "#000000"
                              )}
                            </ListItemIcon>
                            {open && (
                              <>
                                <ListItemText
                                  primary={item.title}
                                  sx={{
                                    opacity: open ? 1 : 0,
                                    "& .MuiTypography-root": {
                                      fontSize: "15px",
                                      fontWeight: "500",
                                      color:
                                        isActive && !open ? "#fff" : "#1D1D1D",
                                    },
                                  }}
                                />
                                <Icon
                                  icon={
                                    openSubmenu[item.id]
                                      ? "mdi:chevron-up"
                                      : "mdi:chevron-down"
                                  }
                                />
                              </>
                            )}
                          </ListItemButton>
                          {open && (
                            <Collapse
                              in={openSubmenu[item.id]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <List component="div" disablePadding>
                                {item.children.map((subItem) => (
                                  <Link
                                    key={subItem.id}
                                    to={`/${subItem.path}`}
                                    style={{
                                      textDecoration: "none",
                                      color: "inherit",
                                    }}
                                  >
                                    <ListItemButton
                                      sx={{
                                        ml: open ? 6.5 : 2,
                                        pl: 3,
                                        height: "48px",
                                        background:
                                          location.pathname ===
                                          `/${subItem.path}`
                                            ? "linear-gradient(to right, #2257AD, #B01121)"
                                            : "transparent",
                                        borderRadius: "320px 0 0 320px",
                                      }}
                                    >
                                      <ListItemText
                                        primary={subItem.title}
                                        sx={{
                                          "& .MuiTypography-root": {
                                            fontSize: "14px",
                                            fontWeight: "400",
                                            color:
                                              location.pathname ===
                                              `/${subItem.path}`
                                                ? "#fff"
                                                : "#1D1D1D",
                                          },
                                        }}
                                      />
                                    </ListItemButton>
                                  </Link>
                                ))}
                              </List>
                            </Collapse>
                          )}
                        </>
                      ) : (
                        <Link
                          to={`/${item.path}`}
                          style={{
                            textDecoration: "none",
                            color: "inherit",
                            position: "relative",
                          }}
                        >
                          {isActive && open && (
                            <Box
                              sx={{
                                height: "48px",
                                width: "27px",
                                background:
                                  "linear-gradient(-90deg, #2257AD 0%, #B01121 100%)",
                                position: "absolute",
                                left: "-15px",
                                borderRadius: "0 320px 320px 0",
                              }}
                            />
                          )}
                          <ListItemButton
                            sx={{
                              justifyContent: open ? "initial" : "center",
                              height: "48px",
                              background:
                                location.pathname === `/${item.path}` && open
                                  ? "linear-gradient(to right, #2257AD, #B01121)"
                                  : "transparent",
                              ml: open ? "15px" : "",
                              borderRadius: "320px 0 0 320px",
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                justifyContent: "center",
                                mr: open ? "15px" : "",
                                m: "",
                                background:
                                  isActive && !open
                                    ? "linear-gradient(to right, #2257AD, #B01121)"
                                    : !isActive && !open
                                    ? "#F4F4F4"
                                    : "transparent",
                                width:
                                  (isActive && !open) || (!isActive && !open)
                                    ? "50px"
                                    : "Auto",
                                height:
                                  (isActive && !open) || (!isActive && !open)
                                    ? "50px"
                                    : "Auto",
                                borderRadius:
                                  (isActive && !open) || (!isActive && !open)
                                    ? "320px"
                                    : "",
                                alignItems:
                                  (isActive && !open) || (!isActive && !open)
                                    ? "center"
                                    : "",
                              }}
                            >
                              {item.icon(
                                (isActive && !open) || (isActive && open)
                                  ? "#fff"
                                  : "#000000"
                              )}
                            </ListItemIcon>
                            {open && (
                              <ListItemText
                                primary={item.title}
                                sx={{
                                  opacity: open ? 1 : 0,
                                  "& .MuiTypography-root": {
                                    fontSize: "15px",
                                    fontWeight: "500",
                                    color:
                                      location.pathname === `/${item.path}`
                                        ? "#fff"
                                        : "#1D1D1D",
                                  },
                                }}
                              />
                            )}
                          </ListItemButton>
                        </Link>
                      )}
                    </ListItem>
                  </React.Fragment>
                );
              })}
            {token && <Divider />}
            {renderDynamicItems([homeItem, ...products])}
          </List>
        </Drawer>

        {/* Mobile Sidebar */}
        <MuiDrawer
          anchor="right"
          variant="temporary"
          open={!openSideBar}
          onClose={() => setOpenSideBar(!openSideBar)}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": { width: 350 },
          }}
        >
          <Box sx={{ mt: "80px" }}>
            {(token || comToken) && (
              <>
                <Box sx={{ p: 2 }}>
                  <ProfileMenu />
                </Box>
                <Divider />
              </>
            )}
            <List>
              {token &&
                filteredNavConfig.map((item) => {
                  const isActive =
                    location.pathname === `/${item.path}` ||
                    (item.children &&
                      item.children.some(
                        (sub) => `/${sub.path}` === location.pathname
                      ));
                  return (
                    <React.Fragment key={item.id}>
                      <ListItem
                        disablePadding
                        sx={{ display: "block", mt: "12px" }}
                      >
                        {item.children ? (
                          <>
                            <ListItemButton
                              sx={{
                                ml: open ? "15px" : "",
                                justifyContent: open ? "initial" : "center",
                                height: "48px",
                              }}
                              onClick={() => {
                                logEvent(
                                  "Click",
                                  `open the dropdown of ${item.title} menu`,
                                  "header"
                                );
                                handleToggleSubmenu(item.id);
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 0,
                                  justifyContent: "center",
                                  mr: open ? "15px" : "",
                                  background:
                                    isActive && !open
                                      ? "linear-gradient(to right, #2257AD, #B01121)"
                                      : open
                                      ? "transparent"
                                      : "#F4F4F4",
                                  width: !open ? "50px" : "Auto",
                                  height: !open ? "50px" : "Auto",
                                  borderRadius: !open ? "320px" : "",
                                  alignItems: !open ? "center" : "",
                                }}
                              >
                                {item.icon(
                                  isActive && !open ? "#fff" : "#000000"
                                )}
                              </ListItemIcon>
                              {open && (
                                <>
                                  <ListItemText
                                    primary={item.title}
                                    sx={{
                                      opacity: open ? 1 : 0,
                                      "& .MuiTypography-root": {
                                        fontSize: "15px",
                                        fontWeight: "500",
                                        color:
                                          isActive && !open
                                            ? "#fff"
                                            : "#1D1D1D",
                                      },
                                    }}
                                  />
                                  <Icon
                                    icon={
                                      openSubmenu[item.id]
                                        ? "mdi:chevron-up"
                                        : "mdi:chevron-down"
                                    }
                                  />
                                </>
                              )}
                            </ListItemButton>
                            {open && (
                              <Collapse
                                in={openSubmenu[item.id]}
                                timeout="auto"
                                unmountOnExit
                              >
                                <List component="div" disablePadding>
                                  {item.children.map((subItem) => (
                                    <Link
                                      key={subItem.id}
                                      to={`/${subItem.path}`}
                                      style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                      }}
                                    >
                                      <ListItemButton
                                        sx={{
                                          ml: open ? 6.5 : 2,
                                          pl: 3,
                                          height: "48px",
                                          background:
                                            location.pathname ===
                                            `/${subItem.path}`
                                              ? "linear-gradient(to right, #2257AD, #B01121)"
                                              : "transparent",
                                          borderRadius: "320px 0 0 320px",
                                        }}
                                      >
                                        <ListItemText
                                          primary={subItem.title}
                                          sx={{
                                            "& .MuiTypography-root": {
                                              fontSize: "14px",
                                              fontWeight: "400",
                                              color:
                                                location.pathname ===
                                                `/${subItem.path}`
                                                  ? "#fff"
                                                  : "#1D1D1D",
                                            },
                                          }}
                                        />
                                      </ListItemButton>
                                    </Link>
                                  ))}
                                </List>
                              </Collapse>
                            )}
                          </>
                        ) : (
                          <Link
                            to={`/${item.path}`}
                            style={{
                              textDecoration: "none",
                              color: "inherit",
                              position: "relative",
                            }}
                          >
                            {isActive && open && (
                              <Box
                                sx={{
                                  height: "48px",
                                  width: "27px",
                                  background:
                                    "linear-gradient(-90deg, #2257AD 0%, #B01121 100%)",
                                  position: "absolute",
                                  left: "-15px",
                                  borderRadius: "0 320px 320px 0",
                                }}
                              />
                            )}
                            <ListItemButton
                              sx={{
                                justifyContent: open ? "initial" : "center",
                                height: "48px",
                                background:
                                  location.pathname === `/${item.path}` && open
                                    ? "linear-gradient(to right, #2257AD, #B01121)"
                                    : "transparent",
                                ml: open ? "15px" : "",
                                borderRadius: "320px 0 0 320px",
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 0,
                                  justifyContent: "center",
                                  mr: open ? "15px" : "",
                                  m: "",
                                  background:
                                    isActive && !open
                                      ? "linear-gradient(to right, #2257AD, #B01121)"
                                      : !isActive && !open
                                      ? "#F4F4F4"
                                      : "transparent",
                                  width:
                                    (isActive && !open) || (!isActive && !open)
                                      ? "50px"
                                      : "Auto",
                                  height:
                                    (isActive && !open) || (!isActive && !open)
                                      ? "50px"
                                      : "Auto",
                                  borderRadius:
                                    (isActive && !open) || (!isActive && !open)
                                      ? "320px"
                                      : "",
                                  alignItems:
                                    (isActive && !open) || (!isActive && !open)
                                      ? "center"
                                      : "",
                                }}
                              >
                                {item.icon(
                                  (isActive && !open) || (isActive && open)
                                    ? "#fff"
                                    : "#000000"
                                )}
                              </ListItemIcon>
                              {open && (
                                <ListItemText
                                  primary={item.title}
                                  sx={{
                                    opacity: open ? 1 : 0,
                                    "& .MuiTypography-root": {
                                      fontSize: "15px",
                                      fontWeight: "500",
                                      color:
                                        location.pathname === `/${item.path}`
                                          ? "#fff"
                                          : "#1D1D1D",
                                    },
                                  }}
                                />
                              )}
                            </ListItemButton>
                          </Link>
                        )}
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              {token && <Divider />}
              {renderDynamicItems([homeItem, ...products])}
            </List>
          </Box>
        </MuiDrawer>
        <Support
          premiumPopupOpen={premiumPopupOpen}
          setPremiumPopupOpen={setPremiumPopupOpen}
        />
      </Box>
    </>
  );
}
